/**
 * The grant's transport restriction, as a three-state choice — **one copy, two surfaces** (D269).
 *
 * §5.2's `transportRestriction` is an optional field: absent means the grant restricts no
 * transport, `'cloud'` and `'ble'` mean it restricts to that one. A control has to offer three
 * options for a field with two values plus an absence, and this module is that translation.
 *
 * **It was written for two controls and now serves one.** P16 gave the invite row a cycling chip
 * and D269 gave the roster's member sheet a three-chip row; the device pass dropped the invite
 * row's (every invite is the default now) and the sheet took over its glyphs. The vocabulary — the
 * order, the label keys, the glyph pairing — stays declared here, so a restriction control added
 * anywhere later reads the same one rather than growing a second copy.
 *
 * **`'any'` is the absence and travels as nothing at all** (D46). It is never a wire value: an
 * invite omits the attribute, and a `PATCH` clears it with an explicit `null`
 * (`packages/functions/members/routes.ts`'s `toGrantPatch`). Do not "simplify" either side into
 * sending the string.
 *
 * The i18n keys are the **`invite` namespace's**, read rather than copied — see `PeopleScreen`'s
 * transport block for why the sheet borrows them instead of minting a `people` set.
 */
import type { TransportKind } from '@selogate/core'
import type { IconName } from '../../components'

/** The three states, in the order both controls present them. */
export const TRANSPORT_ORDER = ['any', 'cloud', 'ble'] as const

export type TransportChoice = (typeof TRANSPORT_ORDER)[number]

/**
 * `as const satisfies` so each value stays a literal the i18n key type can check.
 *
 * The member sheet **draws** the glyphs rather than printing these, so each is that chip's
 * accessible name. All five catalogs keep every string and `check:i18n` still sees the keys used.
 */
export const TRANSPORT_KEYS = {
  any: 'transportAny',
  cloud: 'transportCloudOnly',
  ble: 'transportBleOnly',
} as const satisfies Record<TransportChoice, string>

/**
 * The glyphs: **both transports, cloud alone, BLE alone.** The two-glyph state is why this is a
 * list rather than one name — lucide has no combined mark, and drawing `bluetooth` beside `wifi`
 * says *either way* in the vocabulary the two restricted states already establish, where inventing
 * a third glyph for "any" would not.
 */
export const TRANSPORT_ICONS = {
  any: ['bluetooth', 'wifi'],
  cloud: ['wifi'],
  ble: ['bluetooth'],
} as const satisfies Record<TransportChoice, readonly IconName[]>

/** A stored grant's restriction as the control's three-state choice. */
export function transportChoiceOf(restriction: TransportKind | undefined): TransportChoice {
  return restriction ?? 'any'
}

/**
 * The choice as a `PATCH` body field: a `TransportKind`, or the **explicit `null`** the server
 * reads as *clear this*. `undefined` would mean *leave it alone*, which is the one thing the
 * "any" chip must not do.
 */
export function transportPatchValue(choice: TransportChoice): TransportKind | null {
  return choice === 'any' ? null : choice
}
